/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { Absence, AbsenceSummaryEntry, Department } from '@shared/models';

/** Ein Block der Abwesenheitsliste unter dem Kalender. */
export interface AbsenceDepartmentGroup {
  /** Fehlt bei Personen und Fahrzeugen, die noch keiner Abteilung zugeordnet sind. */
  department?: Department;
  absences: Absence[];
  /** Fehlt, solange der Personal- und Fahrzeugstand nicht geladen ist. */
  summary?: AbsenceSummaryEntry;
}

const WITHOUT_DEPARTMENT = '';

/**
 * Buendelt Abwesenheiten nach der Abteilung des jeweiligen Ziels - Personen und
 * Fahrzeuge stehen dabei im selben Block, weil eine Abteilung beides halten darf.
 *
 * Die Gruppen stehen in der fachlich definierten Reihenfolge (sortOrder, bei Gleichstand
 * alphabetisch); Ziele ohne Abteilung bilden die letzte Gruppe, damit die fehlende
 * Zuordnung sichtbar bleibt, ohne die Gliederung zu stoeren.
 *
 * Innerhalb einer Gruppe wird bewusst nicht sortiert: die Gruppierung ist stabil und
 * uebernimmt damit die Reihenfolge der API.
 */
export function groupAbsencesByDepartment(absences: Absence[]): AbsenceDepartmentGroup[] {
  const groups = new Map<string, AbsenceDepartmentGroup>();

  for (const absence of absences) {
    const department = departmentOf(absence);
    const key = department ? department['@id'] : WITHOUT_DEPARTMENT;
    const group = groups.get(key);

    if (group) {
      group.absences.push(absence);
    } else {
      groups.set(key, { department, absences: [absence] });
    }
  }

  return Array.from(groups.values()).sort(compareGroups);
}

function departmentOf(absence: Absence): Department | undefined {
  return absence.driver ? absence.driver.department : absence.vehicle?.department;
}

function compareGroups(left: AbsenceDepartmentGroup, right: AbsenceDepartmentGroup): number {
  if (!left.department) {
    return right.department ? 1 : 0;
  }

  if (!right.department) {
    return -1;
  }

  return left.department.sortOrder - right.department.sortOrder
    || left.department.name.localeCompare(right.department.name);
}

export function attachAbsenceSummary(
  groups: AbsenceDepartmentGroup[],
  summary: AbsenceSummaryEntry[]
): AbsenceDepartmentGroup[] {
  return groups.map(group => {
    const departmentId = group.department ? group.department['@id'] : null;
    const entry = summary.find(candidate => candidate.department === departmentId);

    return entry ? { ...group, summary: entry } : group;
  });
}
